import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFinance } from '../context/FinanceContext';

export default function NewMonthWizard() {
  const { createMonth, months, fetchRecentMonths } = useFinance();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const template = searchParams.get('template');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  
  const [data, setData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    expected_income: '',
    paysRent: false,
    rent_amount: '',
    hasPrevDebts: false,
    carried_debts: [],
    hasNewDebts: false,
    new_debts: [],
    new_budgets: []
  });
  
  const [newDebtForm, setNewDebtForm] = useState({name: '', amount: '', notes: ''});
  const [newBudgetForm, setNewBudgetForm] = useState({name: '', amount: '', notes: ''});
  const [suggestedIncome, setSuggestedIncome] = useState(false);
  const [suggestedRent, setSuggestedRent] = useState(false);
  
  const autoFilled = useRef(false);

  useEffect(() => {
    if (months.length === 0) {
      fetchRecentMonths();
    } else if (!autoFilled.current) {
      // Months are assumed to be sorted latest first, we take the first active or just first
      const lastMonth = months[0];
      if (lastMonth) {
        setData(prev => ({
          ...prev,
          expected_income: lastMonth.expected_income || prev.expected_income,
          paysRent: lastMonth.rent_amount > 0,
          rent_amount: lastMonth.rent_amount > 0 ? lastMonth.rent_amount : prev.rent_amount
        }));
        if (lastMonth.expected_income > 0) setSuggestedIncome(true);
        if (lastMonth.rent_amount > 0) setSuggestedRent(true);
      }
      autoFilled.current = true;
    }
  }, [months, fetchRecentMonths]);

  const nextStep = () => {
    setError('');
    if(step === 2 && (!data.expected_income || data.expected_income <= 0)) {
      setError('Please enter an amount greater than K0.');
      return;
    }

    if (step === 2 && template && data.new_budgets.length === 0) {
      let generatedBudgets = [];
      if (template === '50_30_20') {
        generatedBudgets = [
          { name: 'Needs (50%)', amount: (data.expected_income * 0.5).toFixed(2), notes: 'Essential living expenses' },
          { name: 'Wants (30%)', amount: (data.expected_income * 0.3).toFixed(2), notes: 'Discretionary spending' },
          { name: 'Savings/Debt (20%)', amount: (data.expected_income * 0.2).toFixed(2), notes: 'Future you' }
        ];
      } else if (template === 'minimalist') {
        generatedBudgets = [
          { name: 'Essentials', amount: (data.expected_income * 0.4).toFixed(2), notes: 'Groceries, Utilities, etc.' },
          { name: 'Bills', amount: (data.expected_income * 0.4).toFixed(2), notes: 'Fixed monthly bills' },
          { name: 'Savings', amount: (data.expected_income * 0.2).toFixed(2), notes: 'Emergency fund / Investments' }
        ];
      }
      setData(d => ({...d, new_budgets: generatedBudgets}));
    }

    setStep(s => s + 1);
  };

  const submit = async () => {
    try {
      const payload = { ...data };
      if(!payload.paysRent) payload.rent_amount = 0;
      const res = await createMonth(payload);
      navigate(`/month/${res.id}`);
    } catch(err) {
      setError(err.response?.data?.error || 'Failed to create month');
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-4 sm:p-8 rounded shadow-sm border border-gray-200 mt-4 md:mt-8">
      <h1 className="text-xl sm:text-2xl font-bold mb-6 text-gray-800">
        Plan New Month {template === '50_30_20' ? '(50/30/20 Rule)' : template === 'minimalist' ? '(Minimalist Template)' : ''}
      </h1>
      
      {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-4">{error}</div>}

      {step === 1 && (
        <div className="space-y-4">
          <label className="block text-gray-700 font-medium">Which month are you planning?</label>
          <div className="flex flex-col sm:flex-row gap-4">
            <select 
              value={data.month} 
              onChange={e => setData({...data, month: e.target.value})}
              className="border border-gray-300 p-2 rounded flex-1"
            >
              {Array.from({length: 12}).map((_, i) => (
                <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('default', {month: 'long'})}</option>
              ))}
            </select>
            <input 
              type="number" 
              value={data.year} 
              onChange={e => setData({...data, year: e.target.value})}
              className="border border-gray-300 p-2 rounded w-32"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <label className="block text-gray-700 font-medium">How much money do you expect this month?</label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500 font-medium">K</span>
            <input 
              type="number" 
              value={data.expected_income} 
              onChange={e => {
                setData({...data, expected_income: e.target.value});
                setSuggestedIncome(false);
              }}
              className="border border-gray-300 p-2 pl-8 rounded w-full"
              placeholder="0.00"
            />
          </div>
          {suggestedIncome && <p className="text-xs text-blue-600 mt-1">Suggested based on your previous month</p>}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <label className="block text-gray-700 font-medium">Do you pay rent?</label>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2">
              <input type="radio" checked={data.paysRent} onChange={() => setData({...data, paysRent: true})} /> Yes
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={!data.paysRent} onChange={() => setData({...data, paysRent: false})} /> No
            </label>
          </div>
          {data.paysRent && (
             <div>
               <div className="relative">
                 <span className="absolute left-3 top-2 text-gray-500 font-medium">K</span>
                 <input 
                   type="number" 
                   value={data.rent_amount} 
                   onChange={e => {
                     setData({...data, rent_amount: e.target.value});
                     setSuggestedRent(false);
                   }}
                   className="border border-gray-300 p-2 pl-8 rounded w-full"
                   placeholder="0.00"
                 />
               </div>
               {suggestedRent && <p className="text-xs text-blue-600 mt-1">Suggested based on your previous month</p>}
             </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <label className="block text-gray-700 font-medium">Do you have additional debts for this month?</label>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2">
              <input type="radio" checked={data.hasNewDebts} onChange={() => setData({...data, hasNewDebts: true})} /> Yes
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={!data.hasNewDebts} onChange={() => setData({...data, hasNewDebts: false})} /> No
            </label>
          </div>
          
          {data.hasNewDebts && (
             <div className="bg-gray-50 p-4 rounded border border-gray-200">
               <div className="grid gap-2 mb-2">
                 <input placeholder="Debt Name" value={newDebtForm.name} onChange={e => setNewDebtForm({...newDebtForm, name: e.target.value})} className="border p-2 rounded" />
                 <input placeholder="Amount" type="number" value={newDebtForm.amount} onChange={e => setNewDebtForm({...newDebtForm, amount: e.target.value})} className="border p-2 rounded" />
                 <input placeholder="Notes" value={newDebtForm.notes} onChange={e => setNewDebtForm({...newDebtForm, notes: e.target.value})} className="border p-2 rounded" />
                 <button onClick={() => {
                   setData({...data, new_debts: [...data.new_debts, newDebtForm]});
                   setNewDebtForm({name: '', amount: '', notes: ''});
                 }} className="bg-gray-200 p-2 rounded font-medium hover:bg-gray-300 text-gray-700">Add Debt</button>
               </div>
               {data.new_debts.length > 0 && (
                 <ul className="mt-4 space-y-2">
                   {data.new_debts.map((d, i) => (
                     <li key={i} className="flex justify-between bg-white p-2 border rounded">
                       <span>{d.name}</span>
                       <span className="font-medium text-red-600">K{d.amount}</span>
                     </li>
                   ))}
                 </ul>
               )}
             </div>
          )}
        </div>
      )}

      {step === 5 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">Summary & Review</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded border border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500">Month</p>
                  <p className="font-medium text-gray-800">{new Date(0, data.month - 1).toLocaleString('default', {month: 'long'})} {data.year}</p>
                </div>
                <button onClick={() => setStep(1)} className="text-xs text-blue-600 hover:underline">Edit</button>
              </div>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500">Expected Income</p>
                  <p className="font-medium text-blue-600">K{data.expected_income || 0}</p>
                </div>
                <button onClick={() => setStep(2)} className="text-xs text-blue-600 hover:underline">Edit</button>
              </div>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500">Rent</p>
                  <p className="font-medium text-gray-800">{data.paysRent ? `K${data.rent_amount}` : 'None'}</p>
                </div>
                <button onClick={() => setStep(3)} className="text-xs text-blue-600 hover:underline">Edit</button>
              </div>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500">New Debts</p>
                  <p className="font-medium text-red-600">{data.hasNewDebts && data.new_debts.length > 0 ? `${data.new_debts.length} debt(s)` : 'None'}</p>
                </div>
                <button onClick={() => setStep(4)} className="text-xs text-blue-600 hover:underline">Edit</button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-bold mb-2">Initial Budgets</label>
            <p className="text-sm text-gray-500 mb-4">
              {template ? "Here are the budgets generated from your template. Feel free to adjust them." : "You can add some starting budgets right now, or do it later."}
            </p>
            <div className="bg-gray-50 p-4 rounded border border-gray-200">
              <div className="grid gap-2 mb-2">
                <input placeholder="Budget Name" value={newBudgetForm.name} onChange={e => setNewBudgetForm({...newBudgetForm, name: e.target.value})} className="border p-2 rounded text-sm" />
                <input placeholder="Amount" type="number" value={newBudgetForm.amount} onChange={e => setNewBudgetForm({...newBudgetForm, amount: e.target.value})} className="border p-2 rounded text-sm" />
                <input placeholder="Notes" value={newBudgetForm.notes} onChange={e => setNewBudgetForm({...newBudgetForm, notes: e.target.value})} className="border p-2 rounded text-sm" />
                <button onClick={() => {
                  if(!newBudgetForm.name || !newBudgetForm.amount) return;
                  setData({...data, new_budgets: [...data.new_budgets, newBudgetForm]});
                  setNewBudgetForm({name: '', amount: '', notes: ''});
                }} className="bg-blue-50 text-blue-700 p-2 rounded font-medium hover:bg-blue-100 text-sm">Add Budget</button>
              </div>
              {data.new_budgets.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {data.new_budgets.map((b, i) => (
                    <li key={i} className="flex justify-between items-center bg-white p-2 border rounded">
                      <div>
                        <span className="font-medium block text-sm">{b.name}</span>
                        <span className="text-xs text-gray-500">{b.notes}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-medium text-blue-600 text-sm">K{b.amount}</span>
                        <button onClick={() => {
                          const arr = [...data.new_budgets];
                          arr.splice(i, 1);
                          setData({...data, new_budgets: arr});
                        }} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-between">
        {step > 1 ? (
          <button onClick={() => setStep(s => s - 1)} className="px-4 py-2 border rounded hover:bg-gray-50 w-full sm:w-auto mb-2 sm:mb-0">Back</button>
        ) : <div className="hidden sm:block"></div>}
        
        {step < 5 ? (
          <button onClick={nextStep} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium w-full sm:w-auto">Next</button>
        ) : (
          <button onClick={submit} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium w-full sm:w-auto">Create Month</button>
        )}
      </div>
    </div>
  );
}
